import React from 'react'
import { Box, Typography, styled } from '@mui/material'

import { pxToRem } from '@/shared/styles/themes'
import { CalloutBlock } from '@/slices/blog/mocks/articles'

export interface ArticleCalloutProps {
  block: CalloutBlock
}

const variantMeta: Record<Exclude<CalloutBlock['variant'], 'quote'>, { label: string, color: string }> = {
  tip: { label: 'Tip', color: '#4A7FA5' },
  info: { label: 'Note', color: '#132848' },
  warning: { label: 'Heads up', color: '#C65D3B' },
}

export const ArticleCallout: React.FC<ArticleCalloutProps> = ({ block }) => {
  const { variant, title, content, attribution, attributionTitle } = block
  const isQuote = variant === 'quote'

  if (isQuote) {
    return (
      <QuoteWrapper>
        <QuoteBlockquote>
          <QuoteText>&ldquo;{content}&rdquo;</QuoteText>
        </QuoteBlockquote>
        {attribution && (
          <QuoteAttribution>
            <AttributionName>{attribution}</AttributionName>
            {attributionTitle && (
              <AttributionTitle>{attributionTitle}</AttributionTitle>
            )}
          </QuoteAttribution>
        )}
      </QuoteWrapper>
    )
  }

  const meta = variantMeta[variant]

  return (
    <CalloutWrapper>
      <CalloutLabel sx={{ color: meta.color }}>{meta.label}</CalloutLabel>
      {title && <CalloutTitle>{title}</CalloutTitle>}
      <CalloutContent>{content}</CalloutContent>
    </CalloutWrapper>
  )
}

const QuoteWrapper = styled(Box)(({ theme }) => ({
  margin: `${pxToRem(32)} 0`,

  [theme.breakpoints.up('md')]: {
    margin: `${pxToRem(40)} 0`,
  },
}))

const QuoteBlockquote = styled('blockquote')({
  margin: 0,
  padding: 0,
})

const QuoteText = styled('p')(({ theme }) => ({
  fontSize: pxToRem(20),
  fontWeight: 700,
  color: theme.palette.primary.main,
  lineHeight: 2,
  display: 'inline',
  backgroundImage: 'linear-gradient(to bottom, #D4E5F0 0%, #D4E5F0 100%)',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 85%',
  backgroundPosition: '0 60%',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
  padding: `${pxToRem(2)} ${pxToRem(4)}`,
  margin: 0,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(24),
  },
}))

const QuoteAttribution = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: pxToRem(8),
  marginTop: pxToRem(20),
})

const AttributionName = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(14),
  fontWeight: 700,
  color: theme.palette.primary.main,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(15),
  },
}))

const AttributionTitle = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(14),
  fontWeight: 400,
  color: theme.palette.text.secondary,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(15),
  },
}))

const CalloutWrapper = styled(Box)(({ theme }) => ({
  margin: `${pxToRem(36)} 0`,
  paddingTop: pxToRem(20),
  borderTop: `1px solid ${theme.palette.divider}`,

  [theme.breakpoints.up('md')]: {
    margin: `${pxToRem(44)} 0`,
    paddingTop: pxToRem(24),
  },
}))

const CalloutLabel = styled('span')({
  display: 'block',
  fontSize: pxToRem(12),
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: pxToRem(10),
})

const CalloutTitle = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(18),
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: pxToRem(8),
  lineHeight: 1.4,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(20),
  },
}))

const CalloutContent = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(17),
  lineHeight: 1.7,
  color: '#2C2A28',

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(19),
    lineHeight: 1.75,
  },
}))
